import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';

interface WorkPlanBoardState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
}

const state: WorkPlanBoardState = {
    loading: false,
    loaded: false,
    fatalError: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: [],
};

export const ITEMS_LIST = 'GET_CLIENT_LIST';

const COLUMNS: any = {
    id: 'products.id',
    product_name: 'products.name',
    name: 'products.name',
    code: 'products.code',
    promo_name: 'products.promo_name',
    archived_at: 'products.archived_at',
    created_at: 'products.created_at',
    updated_at: 'products.updated_at',
    containers: 'grouped_product_containers.containers'
};

function build_query(query: any, page_size?: number) {
    page_size = page_size ? page_size : 20
    const filter: any[] = [];
    if (query.name) {
        filter.push({ "$ilike": ["products.name", {"$": `%${query.name}%`}] })
    }
    return {
        "columns": COLUMNS,
        from: 's:products',
        from_as: 'products',
        joins: [
            {
                join: {
                  columns: {
                    product_id: 'product_containers.product_id',
                    containers: {
                        $array_agg: {
                          $order: [
                            { $asc: ['product_containers.name'] }
                          ],
                          $item: {
                            $obj: {
                              id: 'product_containers.id',
                              name: 'product_containers.name',
                              container_name: 'product_containers.name',
                              archived_at: 'product_containers.archived_at',
                              created_at: 'product_containers.created_at',
                              updated_at: 'product_containers.updated_at',
                            }
                          }
                        }
                      },
                  },
                  from: 's:product_containers',
                  from_as: 'product_containers',
                  groups: [
                    'product_containers.product_id'
                  ],
                },
                type: 'left',
                as: 'grouped_product_containers',
                on: { $eq: ['grouped_product_containers.product_id', 'products.id'] },
            }
        ],
        page_size: page_size,
        ...({page: parseInt(query.page) || 1}),
        ...({
            order: [
                query.sortDirection == 'desc' ? { $desc: [COLUMNS[query.sortField || 'name' ]] } : { $asc: [COLUMNS[query.sortField || 'name']] }
            ]
        }),
        ...(
            filter.length > 1 ?
            {
                "where": {
                    "$and": filter
                }
            } : {
                "where": filter[0]
            }
        )

    }
}

export const actions: ActionTree<WorkPlanBoardState, RootState> = {
    loadData({ commit }, query) {
        const vm = (this as any)._vm;
        console.debug('[ProductDirectory.loadData] START', { query });

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(ITEMS_LIST)
                .raw('documents.list')
                .action(build_query(query))
            ]).then(((result: ServerResult) => {
                const content = result.getOperation(ITEMS_LIST).getContent();
                console.debug('[ProductDirectory.loadData] SUCCESS', {
                    count: (content && content.documents && content.documents.length) || 0,
                    meta: content && content.meta
                });
                commit('SET_ITEMS_LIST', content);
                commit('LOADED_SUCCESS');

                resolve();
            })).catch((error: any) => {
                console.error('[ProductDirectory.loadData] ERROR', { query, error });
                reject(error);
            });
        })
    },

    productToArchive({ rootState }, productId) {
        const vm = (this as any)._vm;
        console.debug('[ProductDirectory.productToArchive] START', { productId });

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('PRODUCT_TO_ARCHIVE')
                    .update(productId, {
                        archived_at: moment().format('YYYY-MM-DDTHH:mm:ss')
                    })
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('PRODUCT_TO_ARCHIVE');

                if (operationResult.hasError()) {
                    console.error('[ProductDirectory.productToArchive] FAIL', { productId });
                    reject();
                } else {
                    console.info('[ProductDirectory.productToArchive] SUCCESS', { productId });
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('[ProductDirectory.productToArchive] ERROR', { productId, error });
                reject(error);
            });
        });
    },

    productToUnarchive({ rootState }, productId) {
        const vm = (this as any)._vm;
        console.debug('[ProductDirectory.productToUnarchive] START', { productId });

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('PRODUCT_TO_ARCHIVE')
                    .update(productId, {
                        archived_at: null
                    })
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('PRODUCT_TO_ARCHIVE');

                if (operationResult.hasError()) {
                    console.error('[ProductDirectory.productToUnarchive] FAIL', { productId });
                    reject();
                } else {
                    console.info('[ProductDirectory.productToUnarchive] SUCCESS', { productId });
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('[ProductDirectory.productToUnarchive] ERROR', { productId, error });
                reject(error);
            });
        });
    },
};

export const mutations: MutationTree<WorkPlanBoardState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_ITEMS_LIST(state, data) {
        state.documents = data.documents;

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
    },
};

const getters = {

};

export const STORE_KEY = '$_product_directory';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
